import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, Loader2, LogOut, PackageCheck, Star } from "lucide-react";
import { toast } from "sonner";
import { SalesCharts } from "@/components/lumi/SalesCharts";
import { StockManager } from "@/components/lumi/StockManager";
import { http, formatApiError } from "@/lib/api";
import { money } from "@/lib/products";
import { Logo } from "@/components/lumi/Logo";
import { ShipDialog } from "@/components/lumi/ShipDialog";

const fmt = (d) => (d ? new Date(d).toLocaleString() : "—");

function LoginForm({ onDone }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await http.post("/auth/login", { email, password });
      if (data?.token) sessionStorage.setItem("lumi_admin_token", data.token);
      onDone();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };
  return (
    <main className="min-h-screen grid place-items-center bg-[#F9ECE8] px-5 pt-20" data-testid="admin-login-page">
      <form onSubmit={submit} className="w-full max-w-md bg-[#FAF7F2] rounded-[32px] border border-[#E6D7CC] p-8 sm:p-10" data-testid="admin-login-form">
        <Logo />
        <h1 className="font-display text-4xl mt-8">Studio access</h1>
        <p className="text-sm text-[#6E504A] mt-2">Private dashboard for orders and the LUMI list.</p>
        <input type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email-input"
          className="mt-8 w-full h-12 rounded-full border border-[#E6D7CC] bg-white px-5 outline-none focus:border-[#4A2E2B]" />
        <input type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input"
          className="mt-3 w-full h-12 rounded-full border border-[#E6D7CC] bg-white px-5 outline-none focus:border-[#4A2E2B]" />
        {error && <p className="text-sm text-red-700 mt-3" data-testid="admin-login-error">{error}</p>}
        <button disabled={loading} data-testid="admin-login-submit" className="mt-6 w-full h-12 rounded-full bg-[#281715] text-[#FAF7F2] flex items-center justify-center hover:bg-[#4A2E2B] transition-colors duration-200">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
        </button>
      </form>
    </main>
  );
}

const authHeaders = () => {
  const t = sessionStorage.getItem("lumi_admin_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const Stat = ({ label, value, testid }) => (
  <div className="rounded-[24px] bg-[#FAF7F2] border border-[#E6D7CC] p-6" data-testid={testid}>
    <p className="overline text-[#6E504A]">{label}</p>
    <p className="font-display text-4xl mt-3">{value}</p>
  </div>
);

const DeliverButton = ({ order, onUpdate }) => {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await http.post(`/admin/orders/${order.session_id}/deliver`, {}, { headers: authHeaders() });
      onUpdate(data);
      toast.success(data?.delivered_email?.startsWith("sent") ? "Delivered — care tips & review request sent" : "Marked delivered (email not sent)");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <button onClick={go} disabled={busy} data-testid={`deliver-btn-${order.session_id}`}
      className="mt-2 flex items-center gap-1.5 whitespace-nowrap rounded-full border border-[#4A2E2B] px-3 h-8 text-xs hover:bg-[#281715] hover:text-[#FAF7F2] transition-colors duration-200">
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PackageCheck className="w-3.5 h-3.5" />} Mark as delivered
    </button>
  );
};

const Fulfilment = ({ order, onUpdate }) => {
  if (order.payment_status !== "paid") return <span className="text-[#6E504A]">—</span>;
  if (order.fulfillment_status === "delivered")
    return (
      <div className="text-xs" data-testid={`delivered-${order.session_id}`}>
        <span className="rounded-full bg-[#281715] text-[#FAF7F2] px-2.5 py-1">Delivered</span>
        <div className="mt-1.5 font-mono-lumi">{order.carrier} · {order.tracking_number}</div>
        <div className="text-[#6E504A]">{order.reviewed ? "Review received" : order.delivered_email?.startsWith("sent") ? "Review request sent" : "Email not sent"}</div>
      </div>
    );
  if (order.fulfillment_status === "shipped")
    return (
      <div className="text-xs" data-testid={`shipped-${order.session_id}`}>
        <span className="rounded-full bg-[#EBB1B6] text-[#281715] px-2.5 py-1">Shipped</span>
        <div className="mt-1.5 font-mono-lumi">{order.carrier} · {order.tracking_number}</div>
        <div className="text-[#6E504A]">{order.shipping_email?.startsWith("sent") ? "Customer emailed" : "Email not sent"}</div>
        <DeliverButton order={order} onUpdate={onUpdate} />
      </div>
    );
  return <ShipDialog order={order} headers={authHeaders()} onShipped={onUpdate} />;
};

function OrdersTable({ orders, onUpdate }) {
  if (!orders.length) return <p className="py-16 text-center text-[#6E504A]" data-testid="orders-empty">No orders yet.</p>;
  return (
    <div className="overflow-x-auto" data-testid="orders-table">
      <table className="w-full text-sm min-w-[960px]">
        <thead><tr className="text-left text-[#6E504A] border-b border-[#E6D7CC]"><th className="py-3 font-medium">Date</th><th className="font-medium">Items</th><th className="font-medium">Customer</th><th className="font-medium">Status</th><th className="font-medium">Receipt email</th><th className="font-medium">Fulfilment</th><th className="font-medium text-right">Amount</th></tr></thead>
        <tbody>
          {orders.map((o) => (
            <tr key={o.session_id} className="border-b border-[#E6D7CC]/70 align-top">
              <td className="py-4 pr-4 whitespace-nowrap">{fmt(o.created_at)}</td>
              <td className="py-4 pr-4">{(o.items ?? []).map((i) => <div key={`${i.product_id}-${i.shade}`}>{i.quantity}× {i.name} · {i.shade}</div>)}</td>
              <td className="py-4 pr-4">{o.customer_email || "—"}</td>
              <td className="py-4 pr-4"><span className={`rounded-full px-3 py-1 text-xs ${o.payment_status === "paid" ? "bg-[#281715] text-[#FAF7F2]" : "bg-[#F9ECE8] text-[#4A2E2B]"}`}>{o.payment_status}</span></td>
              <td className="py-4 pr-4 text-xs" data-testid={`order-email-${o.session_id}`}>{o.confirmation_email?.startsWith("sent") ? "Sent" : o.confirmation_email?.startsWith("failed") ? "Failed" : "—"}</td>
              <td className="py-4 pr-4"><Fulfilment order={o} onUpdate={onUpdate} /></td>
              <td className="py-4 text-right font-display text-base">{money(o.amount)}{o.discount > 0 && <div className="font-sans text-xs text-[#B4566A]">bundle −{money(o.discount)}</div>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function exportCsv(subs) {
  const csv = "email,source,joined\n" + subs.map((s) => `${s.email},${s.source},${s.created_at}`).join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  a.download = "lumi-subscribers.csv";
  a.click();
}

function ReviewsList({ reviews }) {
  if (!reviews.length) return <p className="py-16 text-center text-[#6E504A]" data-testid="reviews-empty">No reviews yet. They arrive after you mark orders as delivered.</p>;
  return (
    <ul className="divide-y divide-[#E6D7CC]" data-testid="reviews-list">
      {reviews.map((r) => (
        <li key={r.id} className="py-4 flex flex-col sm:flex-row sm:items-start justify-between gap-2 text-sm">
          <div>
            <p className="font-display text-lg">{r.name} · {r.shade}</p>
            {r.comment && <p className="text-[#6E504A] mt-1">“{r.comment}”</p>}
            <p className="text-xs text-[#6E504A] mt-1">{r.customer_first || "Customer"} · {fmt(r.created_at)}</p>
          </div>
          <div className="flex shrink-0">{[1, 2, 3, 4, 5].map((n) => <Star key={n} className="w-4 h-4" fill={n <= r.rating ? "#D88B94" : "transparent"} color={n <= r.rating ? "#D88B94" : "#B9A49C"} />)}</div>
        </li>
      ))}
    </ul>
  );
}

function Dashboard({ onLogout }) {
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [subs, setSubs] = useState([]);
  const [reviews, setReviews] = useState([]);
  const stockHeaders = useMemo(() => authHeaders(), []);
  useEffect(() => {
    const h = { headers: authHeaders() };
    http.get("/admin/orders", h).then((r) => setOrders(Array.isArray(r.data) ? r.data : [])).catch(() => onLogout());
    http.get("/admin/subscribers", h).then((r) => setSubs(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    http.get("/admin/reviews", h).then((r) => setReviews(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [onLogout]);
  const paid = useMemo(() => orders.filter((o) => o.payment_status === "paid"), [orders]);
  const revenue = paid.reduce((s, o) => s + (o.amount ?? 0), 0);

  return (
    <main className="min-h-screen bg-[#F4EFE6] pt-[100px] pb-20" data-testid="admin-dashboard">
      <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
        <div className="flex items-end justify-between gap-4">
          <div><p className="overline text-[#6E504A]">LUMI Studio</p><h1 className="font-display text-5xl mt-3">Dashboard</h1></div>
          <button onClick={onLogout} data-testid="admin-logout-btn" className="shrink-0 whitespace-nowrap flex items-center gap-2 text-sm rounded-full border border-[#E6D7CC] bg-[#FAF7F2] px-4 h-10"><LogOut className="w-4 h-4" /> Sign out</button>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
          <Stat label="Paid orders" value={paid.length} testid="stat-paid-orders" />
          <Stat label="Revenue" value={money(revenue)} testid="stat-revenue" />
          <Stat label="Checkouts started" value={orders.length} testid="stat-checkouts" />
          <Stat label="LUMI list" value={subs.length} testid="stat-subscribers" />
        </div>
        <SalesCharts orders={orders} />
        <div className="mt-10 rounded-[28px] bg-[#FAF7F2] border border-[#E6D7CC] p-6">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div className="flex flex-wrap gap-2">
              {[["orders", "Orders"], ["subscribers", "Subscribers"], ["reviews", `Reviews${reviews.length ? ` (${reviews.length})` : ""}`], ["stock", "Stock"]].map(([k, l]) => (
                <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`} className={`rounded-full px-4 h-10 text-sm transition-colors duration-200 ${tab === k ? "bg-[#281715] text-[#FAF7F2]" : "border border-[#E6D7CC]"}`}>{l}</button>
              ))}
            </div>
            {tab === "subscribers" && subs.length > 0 && (
              <button onClick={() => exportCsv(subs)} data-testid="export-subscribers-btn" className="flex items-center gap-2 text-sm"><Download className="w-4 h-4" /> Export CSV</button>
            )}
          </div>
          {tab === "stock" ? <StockManager headers={stockHeaders} /> : tab === "reviews" ? <ReviewsList reviews={reviews} /> : tab === "orders" ? <OrdersTable orders={orders} onUpdate={(u) => setOrders((prev) => prev.map((o) => (o.session_id === u?.session_id ? u : o)))} /> : (
            subs.length ? (
              <ul className="divide-y divide-[#E6D7CC]" data-testid="subscribers-list">
                {subs.map((s) => <li key={s.email} className="py-3 flex justify-between text-sm gap-4"><span>{s.email}</span><span className="text-[#6E504A]">{fmt(s.created_at)}</span></li>)}
              </ul>
            ) : <p className="py-16 text-center text-[#6E504A]" data-testid="subscribers-empty">No subscribers yet.</p>
          )}
        </div>
      </div>
    </main>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    http.get("/auth/me", { headers: authHeaders() }).then(() => setAuthed(true)).catch(() => setAuthed(false));
  }, []);
  const logout = useMemo(() => async () => {
    sessionStorage.removeItem("lumi_admin_token");
    await http.post("/auth/logout").catch(() => {});
    setAuthed(false);
    navigate("/admin");
  }, [navigate]);
  if (authed === null) return <main className="min-h-screen grid place-items-center"><Loader2 className="w-6 h-6 animate-spin" /></main>;
  return authed ? <Dashboard onLogout={logout} /> : <LoginForm onDone={() => setAuthed(true)} />;
}
